import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

/** Every absolute URL we publish is built from this, so they agree everywhere. */
export const SITE_ORIGIN = 'https://oopsy.app';

/** What a page tells the crawlers about itself. */
export interface PageSeo {
   title: string;
   description: string;
   /** Site-root-relative path, e.g. `/how-it-works`. Becomes the canonical URL. */
   path?: string;
   /** Absolute image URL for shares. Defaults to the logo. */
   image?: string;
   /** og:type — 'website' for the app's own pages, 'product' for an offer. */
   type?: string;
   /** True for pages that must never be indexed (signed-in surfaces). */
   noindex?: boolean;
}

const DEFAULT_IMAGE = `${SITE_ORIGIN}/assets/images/og.png`;

/**
 * Keeps the document head in step with the route.
 *
 * Before this existed every URL served the same `<title>Упси!</title>`, which is
 * how a site with several distinct pages ends up represented in search results
 * by one. Google collapses same-titled pages, so the titles have to differ
 * before anything else about indexing is worth attending to.
 *
 * This runs in the browser, so it only fixes the *rendered* DOM. The HTML that
 * arrives from Hosting is fixed separately, by the renderer in
 * `functions/src/seo` — the two deliberately produce the same tags, and this one
 * overwrites whatever the server sent as soon as the app boots.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
   private readonly document = inject(DOCUMENT);

   constructor(private title: Title, private meta: Meta) {}

   /**
    * Apply a page's metadata to the document head.
    *
    * @param {PageSeo} seo - What this page is.
    */
   apply(seo: PageSeo): void {
      const url = `${SITE_ORIGIN}${seo.path ?? '/'}`;
      const image = seo.image ?? DEFAULT_IMAGE;

      this.title.setTitle(seo.title);
      this.meta.updateTag({ name: 'description', content: seo.description });

      // Signed-in surfaces are disallowed in robots.txt too. Both are needed:
      // robots.txt stops the crawl, this stops indexing of a URL that was
      // reached some other way (a shared link, a redirect).
      if (seo.noindex) {
         this.meta.updateTag({ name: 'robots', content: 'noindex, follow' });
      } else {
         this.meta.removeTag('name="robots"');
      }

      this.meta.updateTag({ property: 'og:title', content: seo.title });
      this.meta.updateTag({ property: 'og:description', content: seo.description });
      this.meta.updateTag({ property: 'og:url', content: url });
      this.meta.updateTag({ property: 'og:image', content: image });
      this.meta.updateTag({ property: 'og:type', content: seo.type ?? 'website' });
      this.meta.updateTag({ name: 'twitter:title', content: seo.title });
      this.meta.updateTag({ name: 'twitter:description', content: seo.description });
      this.meta.updateTag({ name: 'twitter:image', content: image });

      this.setCanonical(url);
   }

   /**
    * Point `rel=canonical` at the given URL, creating the link element the first
    * time. One element, rewritten — a second `rel=canonical` makes Google ignore
    * both.
    *
    * @param {string} url - Absolute canonical URL.
    */
   private setCanonical(url: string): void {
      const head = this.document.head;
      let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
         link = this.document.createElement('link');
         link.setAttribute('rel', 'canonical');
         head.appendChild(link);
      }
      link.setAttribute('href', url);
   }

   /**
    * Publish a JSON-LD block for the current page, replacing any previous one.
    *
    * The app reads other shops' structured data for a living (see
    * functions/src/parsing/schema.ts) and published none of its own; this is
    * what lets a search page show a price range in the results instead of a
    * plain blue link.
    *
    * @param {unknown} schema - A schema.org object, or null to remove the block.
    */
   setStructuredData(schema: unknown | null): void {
      const id = 'oopsy-structured-data';
      this.document.getElementById(id)?.remove();
      if (!schema) return;

      const script = this.document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      this.document.head.appendChild(script);
   }
}
